import React, { useEffect } from 'react';
import {   InfoBox,   ExerciseBox,   CodeBlock, CodeEditor, BoldTextParagraph,   InlineCode,   SectionTitle,  Note,  TableContainer,  Highlight,  PatternContainer,  SectionHeading,  SubHeading,  TwoColGrid,  ThreeColGrid,  GridItem,  Divider,  MutedText, Separator, CorrectionBox, Result} from '../../../../components/course/CourseComponents';

const Chapitre8Content = ({ courseId = 'baseprogrammation' }) => {
  
  // Effet pour donner le focus à l'éditeur quand le composant se monte
  useEffect(() => {
    // Le focus sera géré par la prop autoFocus du CodeEditor
  }, []);

  return (
    <>
     <InfoBox title="Objectif" color="blue">
        <p className="mb-0">
        Regrouper du code réutilisable et donner un nom à une action.
        </p>
      </InfoBox>       

      <Separator type="text" text="Qu'est-ce qu'une fonction ?" color="gray-darker" size="md" />
      <SectionTitle>Qu'est-ce qu'une fonction ?</SectionTitle>
      <p>
        Une fonction, c'est un bloc de code qui fait une tâche précise, que tu peux réutiliser plusieurs fois sans tout réécrire.
        C'est très utile pour :
      </p>
      <ul className="list-disc list-inside mb-4">
        <li>éviter de répéter du code</li>
        <li>organiser son programme</li>
        <li>créer des actions réutilisables</li>
      </ul>

      <Separator type="text" text="Créer une fonction simple" color="gray-darker" size="md" />
      <SectionTitle>Créer une fonction simple</SectionTitle>

      <Note type="gray" title="🔧 Créer une fonction :">
        <CodeBlock language="javascript">
{`function direBonjour() {
  print("Bonjour !");
}`}
        </CodeBlock>
        <p  >👉 Ici, la fonction <InlineCode>direBonjour</InlineCode> affiche un message.</p>
      </Note>

      <p>Pour utiliser ta fonction, tu l'appelles comme ceci :</p>

      <Note type="gray" title="📞 Appeler une fonction :">
        <CodeBlock language="javascript">
{`direBonjour(); // appelle la fonction`}
        </CodeBlock>
        <p>👉 Tu peux appeler <InlineCode>direBonjour()</InlineCode> autant de fois que tu veux.</p>
      </Note>

      <Separator type="text" text="Ajouter des paramètres" color="gray-darker" size="md" />
      <SectionTitle>Ajouter des paramètres</SectionTitle>
      <p>
        Tu peux rendre une fonction plus utile en lui donnant des informations à utiliser.
      </p>

      <Note type="gray" title="🔧 Fonction avec paramètre :">
        <CodeBlock language="javascript">
{`function saluer(nom) {
  print("Salut, " + nom + " !");
}

saluer("Mickael");
saluer("Léo");`}
        </CodeBlock>
        <p  >👉 Ici, <InlineCode>nom</InlineCode> est un paramètre.</p>
      </Note>

      <Note type="gray" title="🔢 Fonction avec plusieurs paramètres :">
        <CodeBlock language="javascript">
{`function addition(a, b) {
  print(a + b);
}

addition(3, 4); // 7`}
        </CodeBlock>
        <p>👉 Cette fonction prends 2 paramètres : <InlineCode>a</InlineCode> et <InlineCode>b</InlineCode> et affiche leur somme.</p>
      </Note>

      <Separator type="text" text="Retourner une valeur" color="gray-darker" size="md" />
      <SectionTitle>Retourner une valeur</SectionTitle>
      <p>
        Une fonction peut aussi renvoyer un résultat avec le mot-clé <InlineCode>return</InlineCode>.
      </p>

      <Note type="gray" title="🔁 Fonction qui retourne :">
        <CodeBlock language="javascript">
{`function doubler(x) {
  return x * 2;
}

let resultat = doubler(5);
print(resultat); // 10`}
        </CodeBlock>
        <p  >👉 La fonction prend un paramètre <InlineCode>x</InlineCode> et le multiplie par 2. Il retourne le résultat.</p>
      </Note>

      <Separator type="text" text="Exercices pratiques" color="gray-darker" size="md" />
      <SectionTitle>Exercices pratiques</SectionTitle>


      <Note type="exercice" title="🧪 Exercice fonction avancée :">
        Crée une fonction <InlineCode>soigner</InlineCode> qui prend deux paramètres pv et soin, et renvoie les PV après soin (sans dépasser 100) :<br/>
        - Crée une fonction <InlineCode>soigner(pv, soin)</InlineCode><br/>
        - Additionne pv + soin<br/>
        - Si le total dépasse 100, limite à 100<br/>
        - Retourne le résultat avec <InlineCode>return</InlineCode><br/>
        - Teste avec différentes valeurs<br/>
        <Result>PV après soin : 100</Result>
        
        <CorrectionBox>
          <CodeBlock language="javascript">
{`function soigner(pv, soin) {
  let total = pv + soin;
  if (total > 100) {
    total = 100;
  }
  return total;
}

let vie = soigner(80, 30);
print("PV après soin : " + vie);`}
          </CodeBlock>
        </CorrectionBox>
      </Note>

      <CodeEditor
        courseId={courseId}
        editorId="fonctions-avancees"
        title="Exercice : Fonction avec return"
        autoFocus={true}
        showConsole={true}
        height="300px"
        initialCode=""
      />

      <Separator type="text" text="Fonctions intégrées" color="gray-darker" size="md" />
      <SectionTitle>Les fonctions "intégrées" (built-in)</SectionTitle>
      <p>
        Ce sont les fonctions déjà fournies par JavaScript. Tu peux les utiliser tout de suite, sans rien installer.
      </p>
      <p>
        Voici une liste de fonctions utiles en JavaScript :
      </p>

      <SubHeading>🔢 Fonctions mathématiques (Math.)</SubHeading>
      <TableContainer>
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-800">
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Fonction</th>
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Utilité</th>
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Exemple</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>Math.random()</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Donne un nombre entre 0 et 1</td>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>Math.random() → 0.73</InlineCode></td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>Math.floor(x)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Arrondit vers le bas</td>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>Math.floor(4.9) → 4</InlineCode></td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>Math.ceil(x)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Arrondit vers le haut</td>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>Math.ceil(4.1) → 5</InlineCode></td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>Math.round(x)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Arrondit au plus proche</td>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>Math.round(4.5) → 5</InlineCode></td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>Math.max(a, b, ...)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Le plus grand nombre</td>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>Math.max(2, 5, 8) → 8</InlineCode></td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>Math.min(a, b, ...)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Le plus petit</td>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>Math.min(2, 5, 8) → 2</InlineCode></td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>Math.abs(x)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Valeur absolue</td>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>Math.abs(-7) → 7</InlineCode></td>
            </tr>
          </tbody>
        </table>
      </TableContainer>

      <SubHeading>📏 Fonctions de chaîne de texte (string)</SubHeading>
      <TableContainer>
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-800">
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Fonction</th>
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Utilité</th>
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Exemple</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>"Hello".length</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Longueur du texte</td>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>"Hello".length → 5</InlineCode></td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>"abc".toUpperCase()</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Met en majuscules</td>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>"abc".toUpperCase() → "ABC"</InlineCode></td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>"ABC".toLowerCase()</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Met en minuscules</td>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>"ABC".toLowerCase() → "abc"</InlineCode></td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>"Bonjour".includes("jour")</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Vérifie si un mot est dans un autre</td>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>→ true</InlineCode></td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>"Test".slice(1, 3)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Coupe une partie du texte</td>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>→ "es"</InlineCode></td>
            </tr>
          </tbody>
        </table>
      </TableContainer>

      <SubHeading>📅 Fonctions de date (Date)</SubHeading>
      <Note type="gray" title="🕐 Travailler avec les dates :">
        <CodeBlock language="javascript">
{`let maintenant = new Date();
maintenant.getFullYear(); // année (ex: 2025)
maintenant.getMonth();    // mois (0 = janvier)
maintenant.getDate();     // jour du mois`}
        </CodeBlock>
      </Note>

      <p>
        Teste ici ces fonctions si tu veux voir comment elles fonctionnent :
      </p>

      <CodeEditor
        courseId={courseId}
        editorId="fonctions-integrees"
        title="Exercice : Tester les fonctions intégrées"
        showConsole={true}
        height="300px"
        initialCode={`// Teste les fonctions mathématiques
print(Math.random());
print(Math.floor(4.9));

// Teste les fonctions de texte
print("Hello".length);
print("javascript".toUpperCase());

// Teste les dates
let maintenant = new Date();
print(maintenant.getFullYear());`}
      />


      <Note type="info" title="">
        Toutes les fonctions intégrées JavaScript sont référencées ici :<br/>
        🔗 <a href="https://developer.mozilla.org/fr/docs/Web/JavaScript/Reference" target="_blank" className="text-blue-600 hover:text-blue-800 underline">https://developer.mozilla.org/fr/docs/Web/JavaScript/Reference</a>
      </Note>

      <p>
        ✅ Bravo ! Tu peux maintenant créer des fonctions personnalisées, avec ou sans paramètres, et les réutiliser dans tout ton programme 🚀
      </p>
      
      <BoldTextParagraph>
        Dernière étape : résumé de tout ce que tu as appris 🏁
      </BoldTextParagraph>

  </>
  );
};

export default Chapitre8Content; 